import * as qs from 'qs';

import { nuc } from '../../../../../utils';
import { reportTypeDefaultFormatMap, reportTypeFormatMap } from '../shared/constants';
import { buildExcludeParams, buildFixPlanParams, buildIncludeParams } from '../shared/utils';
import { ReportCategories } from '../utils';
import type { FixPlansParams } from '../../../../../../shared/types/routes/fixPlans';
import type { ReportCategory, ReportExclusionOptions, ReportURLParams } from '../../shared/types';
import type { DependencyMetadataFilterOption, FormatName, IncludeFilterOption, IncludeFixPlanFilterOption } from '../shared/types';

type Query = {
  preview?: boolean;
  download?: boolean;
  emails?: string;
};

type AttributionFormat = 'preview' | 'download' | 'security' | 'public' | 'email';

const getEndpoint = ({
  projectRevisionLocator,
  reportType,
  query,
  format,
  includeOptions,
  dependencyMetadataOptions,
  attributionFormat,
  authorName,
  authorEmail,
  supplier,
  excludeOptions,
  useReportsV2,
}: {
  projectRevisionLocator: string;
  query: Query;
  format: FormatName | null;
  reportType: ReportCategory;
  includeOptions: IncludeFilterOption[];
  dependencyMetadataOptions: DependencyMetadataFilterOption[];
  attributionFormat: AttributionFormat;
  authorName?: string;
  authorEmail?: string;
  supplier?: string;
  excludeOptions?: ReportExclusionOptions;
  useReportsV2?: boolean;
}) => {
  const defaultFileFormat = reportTypeDefaultFormatMap[reportType];
  const formatQuery = format ? reportTypeFormatMap[format] : defaultFileFormat;
  const urlParams: ReportURLParams = {
    ...buildIncludeParams(includeOptions),
    ...(excludeOptions ? buildExcludeParams(excludeOptions) : {}),
    dependencyInfoOptions: dependencyMetadataOptions,
    format: formatQuery,
    authorName,
    authorEmail,
    supplier,
  };
  const queryString = qs.stringify({ ...query, ...urlParams });

  return `/api/${useReportsV2 ? 'v2/' : ''}revisions/${nuc(projectRevisionLocator)}/attribution/${attributionFormat}?${queryString}`;
};

const getFixPlanEndpoint = ({
  projectRevisionLocator,
  format,
  query,
}: {
  projectRevisionLocator: string;
  format: FormatName | null;
  query: FixPlansParams;
}) => {
  const queryString = qs.stringify({
    ...query,
    format: format ? reportTypeFormatMap[format] : reportTypeDefaultFormatMap[ReportCategories.RemediationGuidance],
  });

  return `/api/revisions/${nuc(projectRevisionLocator)}/report/remediation-guidance?${queryString}`;
};

/**
 * Get the preview URL for a report at the project level
 */
export const getPreviewURL = ({
  projectRevisionLocator,
  reportType,
  format,
  includeOptions,
  dependencyMetadataOptions,
  excludeOptions,
  fixPlanOptions,
  authorName,
  authorEmail,
  supplier,
  useReportsV2,
}: {
  projectRevisionLocator: string;
  reportType: ReportCategory;
  format: FormatName | null;
  includeOptions: IncludeFilterOption[];
  dependencyMetadataOptions: DependencyMetadataFilterOption[];
  excludeOptions: ReportExclusionOptions;
  fixPlanOptions: IncludeFixPlanFilterOption[];
  authorName?: string;
  authorEmail?: string;
  supplier?: string;
  useReportsV2?: boolean;
}) => {
  const isVulnerability = reportType === ReportCategories.Vulnerability;
  const isFixPlan = reportType === ReportCategories.RemediationGuidance;

  if (isFixPlan) {
    return getFixPlanEndpoint({
      projectRevisionLocator,
      format,
      query: { ...buildFixPlanParams(fixPlanOptions), preview: true },
    });
  }

  return getEndpoint({
    projectRevisionLocator,
    format,
    query: {
      preview: true,
    },
    reportType,
    includeOptions,
    dependencyMetadataOptions,
    attributionFormat: isVulnerability ? 'security' : 'preview',
    authorName,
    authorEmail,
    supplier,
    excludeOptions,
    useReportsV2,
  });
};

/**
 * Get the download URL for a report at the project level
 */
export const getDownloadURL = ({
  projectRevisionLocator,
  reportType,
  format,
  includeOptions,
  dependencyMetadataOptions,
  fixPlanOptions,
  authorName,
  authorEmail,
  supplier,
  excludeOptions,
  useReportsV2,
}: {
  projectRevisionLocator: string;
  reportType: ReportCategory;
  format: FormatName | null;
  includeOptions: IncludeFilterOption[];
  dependencyMetadataOptions: DependencyMetadataFilterOption[];
  fixPlanOptions: IncludeFixPlanFilterOption[];
  authorName?: string;
  authorEmail?: string;
  supplier?: string;
  excludeOptions?: ReportExclusionOptions;
  useReportsV2?: boolean;
}) => {
  const isVulnerability = reportType === ReportCategories.Vulnerability;
  const isFixPlan = reportType === ReportCategories.RemediationGuidance;

  if (isFixPlan) {
    return getFixPlanEndpoint({
      projectRevisionLocator,
      format,
      query: { ...buildFixPlanParams(fixPlanOptions), bundle: true },
    });
  }

  return getEndpoint({
    projectRevisionLocator,
    format,
    query: {
      download: true,
    },
    reportType,
    includeOptions,
    dependencyMetadataOptions,
    attributionFormat: isVulnerability ? 'security' : 'download',
    authorName,
    authorEmail,
    supplier,
    excludeOptions,
    useReportsV2,
  });
};

/**
 * Get the email URL for a report at the project level
 */
export const getEmailURL = ({
  projectRevisionLocator,
  reportType,
  format,
  includeOptions,
  dependencyMetadataOptions,
  emails,
  authorName,
  authorEmail,
  supplier,
  excludeOptions,
  useReportsV2,
}: {
  projectRevisionLocator: string;
  reportType: ReportCategory;
  format: FormatName | null;
  includeOptions: IncludeFilterOption[];
  dependencyMetadataOptions: DependencyMetadataFilterOption[];
  emails: string;
  authorName?: string;
  authorEmail?: string;
  supplier?: string;
  excludeOptions?: ReportExclusionOptions;
  useReportsV2?: boolean;
}) => {
  return getEndpoint({
    projectRevisionLocator,
    format,
    query: {
      emails,
    },
    reportType,
    includeOptions,
    dependencyMetadataOptions,
    attributionFormat: 'email',
    authorName,
    authorEmail,
    supplier,
    excludeOptions,
    useReportsV2,
  });
};

/**
 * Get the link to generate a public report URL
 */
export const getGenerateReportURL = ({
  projectRevisionLocator,
  reportType,
  format,
  includeOptions,
  dependencyMetadataOptions,
  authorName,
  authorEmail,
  supplier,
  excludeOptions,
  useReportsV2,
}: {
  projectRevisionLocator: string;
  reportType: ReportCategory;
  format: FormatName | null;
  includeOptions: IncludeFilterOption[];
  dependencyMetadataOptions: DependencyMetadataFilterOption[];
  authorName?: string;
  authorEmail?: string;
  supplier?: string;
  excludeOptions?: ReportExclusionOptions;
  useReportsV2?: boolean;
}) => {
  return getEndpoint({
    projectRevisionLocator,
    format,
    query: {},
    reportType,
    includeOptions,
    dependencyMetadataOptions,
    attributionFormat: 'public',
    authorName,
    authorEmail,
    supplier,
    excludeOptions,
    useReportsV2,
  });
};
